import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  Stack,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BlockIcon from '@mui/icons-material/Block'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useReportStore } from '../../stores/reportStore'
import { useDiscussionStore } from '../../stores/discussionStore'
import {
  Report,
  ReportReasonLabels,
  ReportStatus,
  ReportStatusColors,
  ReportStatusLabels,
  ReportTypeLabels,
} from '../../models/Report'

const SNAPSHOT_PREVIEW_LENGTH = 120

function truncate(text: string, length = SNAPSHOT_PREVIEW_LENGTH) {
  if (!text) return '（無內容）'
  return text.length > length ? `${text.slice(0, length)}…` : text
}

type StatusFilter = 'all' | ReportStatus

export default function ReportListPage() {
  const { reports, loading, error, fetchReports, updateStatus } =
    useReportStore()
  const { deleteDiscussion, deleteReply } = useDiscussionStore()

  const [filter, setFilter] = useState<StatusFilter>('pending')
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  const pendingCount = useMemo(
    () => reports.filter((r) => r.status === 'pending').length,
    [reports]
  )

  const visibleReports = useMemo(
    () =>
      filter === 'all' ? reports : reports.filter((r) => r.status === filter),
    [reports, filter]
  )

  const handleDeleteContent = async (report: Report) => {
    const label = ReportTypeLabels[report.type]
    if (
      !window.confirm(
        `確定要刪除這則被檢舉的${label}嗎？此操作無法復原，刪除後檢舉將標記為已處理。`
      )
    ) {
      return
    }

    setBusyId(report.id)
    try {
      if (report.type === 'reply' && report.replyId) {
        await deleteReply(report.discussionId, report.replyId)
      } else {
        await deleteDiscussion(report.discussionId)
      }
      await updateStatus(report.id, 'resolved')
    } catch {
      // 錯誤已由 store 記錄
    } finally {
      setBusyId(null)
    }
  }

  const handleUpdateStatus = async (report: Report, status: ReportStatus) => {
    setBusyId(report.id)
    try {
      await updateStatus(report.id, status)
    } catch {
      // 錯誤已由 store 記錄
    } finally {
      setBusyId(null)
    }
  }

  if (loading && reports.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          檢舉管理
        </Typography>
        {pendingCount > 0 && (
          <Chip
            label={`${pendingCount} 筆待處理`}
            color="warning"
            size="small"
          />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        使用者透過 App 檢舉的討論區內容。請於 24 小時內處理，符合 App Store
        審核指南 1.2 對使用者產生內容的要求。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={filter}
        onChange={(_, value: StatusFilter) => setFilter(value)}
        sx={{ mb: 2 }}
      >
        <Tab value="pending" label={`待處理 (${pendingCount})`} />
        <Tab value="resolved" label="已處理" />
        <Tab value="dismissed" label="已駁回" />
        <Tab value="all" label={`全部 (${reports.length})`} />
      </Tabs>

      {visibleReports.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">暫無檢舉紀錄</Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>時間</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>類型</TableCell>
                <TableCell sx={{ minWidth: 240 }}>被檢舉內容</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>原因</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>檢舉者</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>狀態</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleReports.map((report) => (
                <TableRow key={report.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {format(report.createdAt, 'yyyy/MM/dd HH:mm', {
                      locale: zhTW,
                    })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ReportTypeLabels[report.type]}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={report.contentSnapshot || ''}>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {truncate(report.contentSnapshot)}
                      </Typography>
                    </Tooltip>
                    {report.detail && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        檢舉說明：{report.detail}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {ReportReasonLabels[report.reason] ?? report.reason}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={report.reporterId}>
                      <Typography variant="caption">
                        {report.reporterId.slice(0, 8)}…
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ReportStatusLabels[report.status]}
                      size="small"
                      color={ReportStatusColors[report.status]}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={busyId === report.id}
                        onClick={() => handleDeleteContent(report)}
                      >
                        刪除內容
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        disabled={
                          busyId === report.id || report.status === 'resolved'
                        }
                        onClick={() => handleUpdateStatus(report, 'resolved')}
                      >
                        標記已處理
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<BlockIcon />}
                        disabled={
                          busyId === report.id || report.status === 'dismissed'
                        }
                        onClick={() => handleUpdateStatus(report, 'dismissed')}
                      >
                        駁回
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
